import React from "react";
import { Navigate } from "react-router-dom";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useAuth } from "../auth/AuthContext";
import { Permission } from "../types/Auth";

interface PrivateRouteProps {
  children: React.ReactNode;
  /** Permission(s) required to view this route. ANY-OF semantics. */
  permission?: Permission | Permission[];
}

/**
 * Route guard: requires a valid token AND (optionally) a permission.
 * Token check redirects to /login; permission check renders a Not Authorized
 * panel rather than redirecting, so the URL stays meaningful.
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, permission }) => {
  const token = localStorage.getItem("jwtToken");
  const { loading, hasAnyPermission } = useAuth();

  if (!token) return <Navigate to="/login" replace />;

  if (!permission) return <>{children}</>;

  // Wait for /admin/me before deciding on permission-gated routes.
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const required = Array.isArray(permission) ? permission : [permission];
  if (!hasAnyPermission(required)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 440, textAlign: "center" }} elevation={2}>
          <LockIcon color="warning" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>Not authorized</Typography>
          <Typography variant="body2" color="text.secondary">
            Your role doesn't have access to this section. Contact a super admin if you believe this is a mistake.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;
